import { setEnv } from "./constants";
import * as XLSX from 'xlsx'
import moment from 'moment';
import { html } from 'lit';
import parse from 'html-react-parser';
import { getEnvironment } from "./amplitude";
import { CookieService } from "../services/cookie.service";

export const Utils = {

  /**
   * Formats campaign title with synthetic ID
   * @param {string} campaignName - The campaign name
   * @param {string} syntheticId - The synthetic ID
   * @returns {string} Formatted title
   */
  formatCampaignTitle: (campaignObj) => {
    let syntheticId = campaignObj?.parent_id ? campaignObj.synthetic_id : null;
    let campaignName = campaignObj?.campaign_display_name || campaignObj?.campaign_name || campaignObj?.campaignName;

    if (!campaignName) return '';
    if (!syntheticId || syntheticId === '-') return campaignName;
    // remove syntheticId for now will update in onther release
    return `${campaignName}:|${syntheticId}`;

  },

  formatDateObject: (dateInput) => {
    if (!dateInput) return "";

    let parsedDate;
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    if (typeof (dateInput) === 'string') {
      // Convert dateInput from yyyy-mm-dd to yyyy/mm/dd format
      let formattedDate = "";
      const dateParts = dateInput.split("-");
      dateParts.forEach((part, index) => {
        if (index === dateParts.length - 1) {
          formattedDate += part;
        } else {
          formattedDate += part + "/";
        }
      });
      parsedDate = dateInput ? new Date(formattedDate) : null;
    } else {
      parsedDate = dateInput ? new Date(dateInput) : null;
    }

    // Validate if parsedDate is a valid Date object
    if (!(parsedDate instanceof Date && !isNaN(parsedDate.valueOf()))) {
      parsedDate = dateInput ? new Date(dateInput) : null;
    }

    const day = parsedDate.getDate();
    const monthIndex = parsedDate.getMonth();
    const monthName = monthNames[monthIndex];
    const year = parsedDate.getFullYear();

    return `${monthName} ${day}, ${year}`;
  },

  formatCalenderDate: (value) => {
    if (value) {
      const date = new Date(value); //convert the date string to Date object
      const options = { month: 'short', day: 'numeric', year: 'numeric' }
      const formattedDate = date.toLocaleDateString('en-US', options)
      return formattedDate;
    } else {
      return "";
    }
  },

  // navigates to the specific path using the navigate() in props
  navigateToComponent: (campaign, path, props, totalInOverallBudget = {}, parentCampaign = '') => {
    const campaignId = encodeURIComponent(campaign?.id);
    let state;
    if (parentCampaign) {
      state = { campaignObj: campaign, parentCampaign: parentCampaign };  
    } else {
      state = { campaignObj: campaign };
    }

    if (Object.keys(totalInOverallBudget).length) {
      state.totalInOverallBudget = totalInOverallBudget;
    }
  
    props.navigate(`/${path}/${campaignId}`, { state });
  },

  /**
   * Sets the base URL (environment) based on the current window location host.
   * The function uses Promises for asynchronous operations and resolves the Promise
   * once the environment is set using the setEnv() function.
   * @async
   * @function setBaseUrl
   * @returns {Promise<void>} A Promise that resolves when the environment is set.
   */
  setBaseUrl: () => {
    return new Promise(function (resolve, reject) {
      const host = window.location.host;
      switch (host) {
        case 'qamediaops.annalect.com':
        case 'qaomni.annalect.com':
          setEnv('qa');
          resolve();
          break;
        case 'stgmediaops.annalect.com':
        case 'stgomni.annalect.com':
          setEnv('stg');
          resolve();
          break;
        case 'mediaops.annalect.com':
        case 'omni.annalect.com':
          setEnv('prod');
          resolve();
          break;
        default:
          setEnv('dev');
          resolve();
          break;
      }
    });
  },
  /**
   * To get Object from array if value match with any object
   * @param {any} value to match with object 
   * @param {Array} array run loop to get value from array
   * @param {string} key key of object to match value from object with pram value
   * @returns {object} obj return obj that get from array
   */
  getObjectFromArray(value, array, key) {
    let obj = array.find(val => {
      if (val[key] && value) {
        return val[key]?.toLowerCase() == value.toLowerCase();
      } else {
        return null
      }
    });;
    return obj;
  },
  
  /**
  * Sorts an array of data based on string and boolean values.
  * This function sorts the provided data array based on the key specified in the event detail.
  * If the key is 'updatedBy', it falls back to 'createdBy' if 'updatedBy' is not present.
  * If the key is 'status', it sorts based on the 'isActive' boolean value.
  * For other keys, it sorts based on the string values in a case-insensitive manner.
  * @param {Object} event - The event object containing the key to sort by.
  * @param {Array} data - The array of data to be sorted.
  * @returns {Array} The sorted array of data.
  */
 sortStringAndBooleanValues: (event, data) => {
   return data.sort((objectA, objectB) => {
     if (event.detail.key === 'updatedBy') {
       // Use 'createdBy' if 'updatedBy' is not present
       let keyNameOne = !objectA[event.detail.key] ? 'createdBy' : event.detail.key;
       let keyNameTwo = !objectB[event.detail.key] ? 'createdBy' : event.detail.key;
       return objectA[keyNameOne]?.toLowerCase() < objectB[keyNameTwo]?.toLowerCase() ? -1 : 1;
      } else {
        if (event.detail.key === 'status') {
          // Sort based on the 'isActive' boolean value
          return objectA['isActive'] < objectB['isActive'] ? 1 : -1;
        } else {
          const makeLowerCaseValue = (value) => typeof value === "string" ? value?.toLowerCase() : value;
          // Sort based on the string values in a case-insensitive manner
          return makeLowerCaseValue(objectA[event.detail.key]) < makeLowerCaseValue(objectB[event.detail.key]) ? -1 : 1;
        }
      }
    });
  },

  /**
  * Sorts an array of data based on date values.
  * This function sorts the provided data array based on the key specified in the event detail.
  * If the key is 'updatedAt', it falls back to 'createdAt' if 'updatedAt' is not present.
  * For other keys, it sorts based on the date values directly.
  * @param {Object} event - The event object containing the key to sort by.
  * @param {Array} data - The array of data to be sorted.
  * @returns {Array} The sorted array of data.
  */
 sortDateValues: (event, data) => {
   return data.sort((objectA, objectB) => {
     if (event.detail.key == 'updatedAt') {
       // Use 'createdAt' if 'updatedAt' is not present
       let keyNameOne = !objectA[event.detail.key] ? 'createdAt' : event.detail.key;
       let keyNameTwo = !objectB[event.detail.key] ? 'createdAt' : event.detail.key;
       return new Date(objectA[keyNameOne]) - new Date(objectB[keyNameTwo]);
      } else {
        // Sort based on the date values directly
        return new Date(objectA[event.detail.key]) - new Date(objectB[event.detail.key]);
      }
    });
  },
 
  /**
  * Determines whether the publish button should be disabled based on various conditions.
  * Checks conditions related to the template name, dates, column names, and plan IDs.
  * Returns `true` if any condition requires the button to be disabled, otherwise `false`.
  * @returns {boolean} `true` if the publish button should be disabled, `false` otherwise.
  */
 disableHierarchyPublishButton: (state, tableRowData, existingColumnsIsUpdated, enableButton = false) => {
  const templateNameIsEmpty = state.templateName?.trim() === '';
  const templateNameIsUpdated = (tableRowData?.templateName !== state.templateName);
  const endDateIsUpdated = (state.endDate && tableRowData?.campaignEnd !== state.endDate);
  const startDateIsUpdated = (state.startDate && tableRowData?.campaignStart !== state.startDate);
  const someColumnNameIsEmpty = state.hierarchyObjectData?.some(item => item.columnName === '');
  const someCustomColumnNameIsEmpty = state.customColumnsData?.some(item => item.columnName === '');
  // Checks if `planIds` in `tableRowData` matches `state.planIds` in length and content
  // defaulting to an empty array if `tableRowData.planIds` is null or undefined
  const planIdsIsUpdated = ((tableRowData?.planIds || [])?.length === state.planIds?.length) && ((tableRowData?.planIds || [])?.every(item => state.planIds.includes(item)));
  let disabled = false; // Check if the template name is updated, or if the hierarchy object data is available
  // If the button is enabled, or the template name is updated, or the hierarchy object data is available, or the existing columns are updated, or the start date is updated, or the end date is updated
  if ((enableButton && tableRowData?.mediaOpsState === "Saved") || templateNameIsUpdated || state.hierarchyObjectData?.length || existingColumnsIsUpdated || startDateIsUpdated || endDateIsUpdated) 
    disabled = templateNameIsEmpty || someColumnNameIsEmpty; // Disable the button if the template name is empty or some column name is empty
  else if (templateNameIsEmpty || someColumnNameIsEmpty || someCustomColumnNameIsEmpty || planIdsIsUpdated) 
    disabled = true;
  return disabled;
},

/**
 * Transforms an array of data objects into a specific payload format for columns.
 * @param {Array} data - The array of data objects to be transformed.
 * @returns {Array} - An array of objects formatted with specific column properties.
 */
createColumnsPayload: (data) => {
  return data.map((item) => {
    //sometime value is missing, condition is use to send defaul value true if missing
    let isAGroupByColumn = item?.isAGroupByColumn == false ? false : true
    return {
      dataType: "text",
      tier: item?.type,
      tierOrder: item?.level,
      sourceSys: "planit",
      columnName: item?.columnName,
      isPartOfTierId :item.uniqueChecked,
      isMinSetColumn: item.isMinSetColumn || false,
      isAGroupByColumn: isAGroupByColumn,
      isPartOfDisplayName:item.isPartOfDisplayName,
      sourceColumnName: item?.sourceColumnName,   
    }
  })
},
  
  /**
   * function is used to create nested array with related child placement
   * @param {Array} list 
   * @param {string} parentIdKey 
   * @param {number} id 
   * @returns 
   */
  createNestedArray (list, parentIdKey,id) {
    let parentChildMap = {};
    let nestedList = [];

    for(let i=0;i<list.length;i++){
      if(list[i][parentIdKey]){
        let parent = Number(list[i][parentIdKey]);
        if(!parentChildMap[parent]){
          parentChildMap[parent] = [];
        }
        parentChildMap[parent].push(list[i]);
      }
    }
    
    for(let i=0;i<list.length;i++){
      if(parentChildMap[list[i].id]){
        list[i].nestedData = parentChildMap[list[i].id];
      }
    }

    nestedList = list.filter(item => !item[parentIdKey]);
    return nestedList[0].nestedData;
  },
  /** `numberToCurrencyString` is a function that takes a number as an argument and returns a formatted
  * currency string. It first checks if the argument is a string, and if so, converts it to a number.
  * If the resulting number is NaN (not a number), it returns an empty string. If the argument is
  * already a number, it uses the `toLocaleString` method to format the number as a currency string
  * with USD as the currency, and a minimum of 2 and maximum of 2 decimal places. Finally, it returns
  * the formatted string. 
  * @param {string | number} number - any currency in string or number format
  * @returns {string} - returns number in currency format $0,000,00.00 format
  */
  numberToCurrencyString: (number) => {
    let num = number;
    if (typeof number === 'string') {
      num = Number(number);
      if (isNaN(num)) {
        return '';
      }
    }
    if (typeof num === 'number') {
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return ''
  },

  /**
 * Checks if a value is a valid number.
 *
 * @param {any} value - The value to be checked.
 * @returns {boolean} Returns true if the value is a valid number, false otherwise.
 */
  isValidNumber: (value) => !isNaN(value),

  /**
   * Function to create and download an excel sheet with the provided data
   * @param {Array} exportData An array of objects contains the excel data
   * @param {string} sheetName Name of the sheet in excel
   * @param {string} fileName File name of the exported excel
   */
  exportToExcel: (exportData, sheetName, fileName) => {
    /* create worksheet */
    const ws = XLSX.utils.json_to_sheet(exportData);
    /* create workbook and export */
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  },

  /**
   * Function to calculate the width of the taxonomy string in px based on the length of the string.
   * Max width is 500px and if the string length is greater than 500px then it will return the half of the string length
   * @param {string} taxonomy - taxonomy string
   * @returns {string} - width in px
   */
  calculatetaxonomyWidth: (taxonomy, isPlacementTable) => {
    let spanEl = document.createElement("SPAN");
    let taxnomyText = document.createTextNode(taxonomy);
    spanEl.appendChild(taxnomyText);
    document.body.appendChild(spanEl);
    spanEl.style.whiteSpace = 'nowrap';
    let taxonomyWidth = spanEl.offsetWidth;
    document.body.removeChild(spanEl);
    if (taxonomyWidth < 500 && isPlacementTable) return '500px';
    else if (taxonomyWidth < 500 && !isPlacementTable) return taxonomyWidth + 50 + 'px';
    else {
      if ((taxonomyWidth / 2 + 50) < 500) return '500px';
      else return taxonomyWidth / 2 + 50 + 'px';
    }
  },

  /**
   * Function to parse date to required format
   * @param {string} date - date string
   * @param {string} format - format to which date needs to be parsed
   * @returns {string} - formatted date
  */
  parseDateToFormat: (date, format) => {
    let formattedDate = '';
    if (date) {
      formattedDate = moment(date, "YYYY-MM-DD").format(format ? format.toUpperCase() : 'MM DD,YYYY');
    } else {
      formattedDate = format ? format.toUpperCase() : 'MM DD,YYYY'
    }
    return formattedDate;
  },

  /*
    * Function to sort the provided data based on the key(of type number) and direction
    * @param {Array} data - Array of objects to be sorted
    * @param {String} key - Key based on which the data should be sorted
    * @param {String} dir - Direction of sorting (asc or desc)
    * @returns {Array} - Sorted data
    */
  sortNumber: (data, key, dir) => {
    let sortedData = [];
    return sortedData = data.sort((a, b) => {
      if (dir === 'desc') {
        return b[key] - a[key];
      }
      return a[key] - b[key];
    });
  },

  /*
  * Function to sort the provided data based on the key(of type string) and direction
  * @param {Array} data - Array of objects to be sorted
  * @param {String} key - Key based on which the data should be sorted
  * @param {String} dir - Direction of sorting (asc or desc)
  * @returns {Array} - Sorted data
  */
  sortString: (data, key, dir) => {
    let sortedData = [];
    return sortedData = data.sort((a, b) => {
      let x = a[key].toLowerCase();
      let y = b[key].toLowerCase();
      if (dir === 'desc') {
        if (x > y) { return -1; }
        if (x < y) { return 1; }
      } else {
        if (x > y) { return 1; }
        if (x < y) { return -1; }
      }
      return 0;
    });
  },

  /*
  * Function to sort the provided data based on the key(of type DATE) and direction
  * @param {Array} data - Array of objects to be sorted
  * @param {String} key - Key based on which the data should be sorted
  * @param {String} dir - Direction of sorting (asc or desc)
  * @returns {Array} - Sorted data
  */
  sortDate: (data, key, dir) => {
    let sortedData = [];
    return sortedData = data.sort((a, b) => {
      if (dir === 'desc') {
        return new Date(b[key]) - new Date(a[key]);
      } else {
        return new Date(a[key]) - new Date(b[key]);
      }
    });
  },

  /**
 * The below code is defining a function called `convertDates` which takes in four parameters:
 * `match`, `year`, `month`, and `day`. The function then creates a new date object and sets
 * the year, month, and day of the date object to the values of the `year`, `month`, and `day`
 * parameters. The function then splits the `match` parameter by the colon character and
 * retrieves the first element of the resulting array. The function then returns a string
 * containing the `matchKey` and the ISO string of the date object.
 */
  convertDates: (match, year, month, day) => {
    const date = new Date();
    date.setFullYear(year, month - 1, day);
    const matchKey = match.split(':')[0];
    return `${matchKey}: "${date.toISOString()}"`;
  },

  /**
   * Return date input with all attribute need for date
   * get event value and then get value from event return back string value
   * @param {object} event 
   * @returns return string value from event
   */
  getValue:(event)=>{    
    let value = ''
    if(Array.isArray(event.target.value) && typeof event.target.value[0] === 'string'){
      value = event.target.value[0]
    }else if(Array.isArray(event.target.value) && typeof event.target.value[0] === 'object'){
      value = event.target.value[0].id
    }else{
      value = event.target.value
    }
    return value;
  },
   
  /* Return date input with all attribute need for date
   * @param {Object} dateInputValues all attribute value for input
   * @param {object} changeFn functino call on change 
   * @param {object} value object all detail use if need in change function
   * @returns date input html 
   */
  createDateInput:(dateInputValues,changeFn,value = null) => {
    return html`<input
      class="input is-static is-editable"
      part="date-input"
      ?disabled=${dateInputValues.disabled}
      data-date=${dateInputValues.value?Utils.formatDateObject(dateInputValues.value) :"Mon DD, YYYY"}
      id=placement-flight-start-date-${dateInputValues.index}
      type='date'
      .value=${dateInputValues.value}
      min=${dateInputValues.min}
      max=${dateInputValues.max}
      activeStartDate=${dateInputValues.value}
      @change=${(event) => changeFn(dateInputValues.inputLabel, event, value, dateInputValues.index)}
      @click=${(event) => Utils.onDateChange(event,dateInputValues.value,)}
      @blur=${(event) => Utils.onDateChange(event,dateInputValues.value,)}
    >`
  },

  /**
   * Date input doesn't have on select event , So reset event value on blur and click if value got changed
   * @param {object} event 
   * @param {string} value 
   */
  onDateChange:(event,value)=>{
    event.target.value = value;
  },

  /**
   * Camparing both parents and Child start and end date,
   * Child date should come under Parent date range or match parent date range. 
   * @param {Object} parentCampaign 
   * @param {Array} childCampaigns 
   * @returns 
   */
  compareDates : (parentCampaign,childCampaigns) => {
    let isDateMatched = true;
    const parentStartDate = new Date(parentCampaign.start_date);
    const parentEndDate = new Date(parentCampaign.end_date);
   
    for(let childCampaignObj of childCampaigns){
        let childStartDate = new Date(childCampaignObj.start_date);
        let childEndDate = new Date(childCampaignObj.end_date);
        if(parentStartDate > childStartDate || parentEndDate < childEndDate){
            isDateMatched = false;
            break;
        }
    }
    return isDateMatched;
  },

  /**
   * if it's USD currancy it will convert in doller 
   * else it will add currany name and local budget value
   * @param {String} currencyName 
   * @param {number} budget 
   * @returns  currency name and budget value in string
   */
  getLocalCurrency(currencyName, budget) {
    if (currencyName === 'USD') {
      return budget?.toLocaleString('en-US', {style: "currency", currency: "USD"})
    } else {
      return budget?.toLocaleString('en-US', {style: "currency", currency: currencyName});
    }
  },

  /**
   * Function to apply multiple inline styles to an HTML element during runtime.
   * @param {HTMLElement} elementHTML - The HTML element to which the styles should be applied.
   * @param {Object} styles - An object containing the styles to be applied.
  */ 
  assignInlineStyleOnRuntime: (elementHTML, styles) => {
    let stylesKeys = Object.keys(styles);
    for(let i=0;i<stylesKeys.length;i++){
        elementHTML.style[stylesKeys[i]] = styles[stylesKeys[i]];
    }
  },

  /**
   * Function to wait for an element to be rendered in the DOM.
   * This function uses a MutationObserver to observe changes in the parent node.
   * @param {HTMLElement} parentNode - The parent node to observe for changes.
   * @param {HTMLElement} elmToWait - The element to wait for.
   * @returns {Promise} - A promise that resolves when the element is rendered.
  */ 
  waitForElm: (parentNode, elmToWait) => {
    return new Promise(resolve => {
      if (elmToWait) {
        return resolve(elmToWait);
      }

      const observer = new MutationObserver(mutations => {
        if (elmToWait) {
          observer.disconnect();
          resolve(elmToWait);
        }
      });

      observer.observe(parentNode, {
        childList: true,
        subtree: true
      });
    });
  },

  /**
  * Transforms the amounts in the provided object by removing the $ symbol and commas, and converts them to numbers.
  *
  * @param {Object} obj - The object containing the amounts as strings.
  * @returns {Object} - The transformed object with amounts as numbers.
  */
  transformAmounts: (obj) => {
    const transformedObj = { ...obj };
    Object.keys(transformedObj).forEach(key => {
      if (typeof transformedObj[key] === 'number') {
        transformedObj[key] = parseFloat(transformedObj[key]);
      }
    });

    return transformedObj;
  },
  
  /**
   * Display the table skeleton
   * @returns {void}
  */
 displayTableSkeleton: () => {
   let rows = '';
   for (let i = 0; i < 10; i++) {
     rows = rows + '<div className="table skeleton"></div>';
    }
    rows = (html`${rows}`);
    return <>{parse(rows.values[0])}</>;
  },

  /**
   * Splits a string by multiple delimiters.
   *
   * This function takes a string and an array of delimiters, and splits the string by any of the delimiters.
   * The delimiters are included in the resulting array.
   *
   * @param {string} str - The string to be split.
   * @param {Array<string>} delimiters - An array of delimiters to split the string by.
   * @returns {Array<string>} An array of substrings, including the delimiters.
   */
  splitByDelimiters: (str, delimiters) => {
    const regex = new RegExp(`([${delimiters.join('')}])`);
    return str?.split(regex).filter(Boolean);
  },

  /**
   * Highlights differences between multiple strings.
   *
   * This function takes an object of strings and an array of delimiters, and highlights the differences between the strings.
   * The differences are wrapped in a <span> tag with a specific part attribute for styling.
   *
   * @param {Object} stringsObj - An object where keys are identifiers and values are the strings to be compared.
   * @param {Array<string>} delimiters - An array of delimiters to split the strings by.
   * @returns {Object} An object where keys are the same as the input object and values are arrays of HTML elements with highlighted differences.
   */
  highlightDifferences: (stringsObj, delimiters, taxonomyFields) => {
    const taxonomyfieldSourceString = taxonomyFields.map(field => field?.source || 'null').join('_');
    const taxonomySourceParts = Utils.splitByDelimiters(taxonomyfieldSourceString, ['_']);
    const keys = Object.keys(stringsObj);
    let strings = keys.map(key => stringsObj[key]);
    const maxLength = Math.max(...strings.map(str => Utils.splitByDelimiters(str, delimiters)?.length));
    const results = {};

    keys.forEach(key => {
      results[key] = [];
    });

    for (let i = 0; i < maxLength; i++) {
      const parts = strings.map(str => Utils.splitByDelimiters(str, delimiters)[i] || '');
      const allEqual = parts.every(part => part === parts[0]);

      keys.forEach((key, j) => {
        if (delimiters.includes(parts[j])) {
          results[key].push(html`<span>${parts[j]}</span>`);
        } else if (allEqual) {
          results[key].push(html`<span>${parts[j]}</span>`);
        } else if (taxonomySourceParts[i]?.toLowerCase() === 'planit') {
          results[key].push(html`<span part="highlight-difference">${parts[j]}</span>`);
        } else {
          results[key].push(html`<span>${parts[j]}</span>`);
        }
      });
    }

    return results;
  },

  /**
   * Handles the logic for swapping JSON keys and values
   * @function swapJson
   * @param {Object} json - The JSON object to swap
   * @returns {Object} - The new object with swapped keys and values
   */
  swapJson: (json) => {
    var ret = {};
    for(var key in json){
      if(Array.isArray(json[key])) {
        json[key].forEach(item => {
          ret[item] = key;
        });
      } else {
        ret[json[key]] = key;
      }
    }
    return ret;
  },
  
  /**
   * open export download modal
   * @param {object} exportResponse after job get completed
   */
  showDownloadModal : (exportResponse ) => {
    let omniDialogEl = document.getElementById('export-placement-dialog'); 
    let downloadBtn = document.getElementById('export-placement-download-btn'); 
    downloadBtn.href = exportResponse.s3PresignedUrl;
    omniDialogEl.showModal();
    omniDialogEl.shadowRoot.querySelector('dialog').style.width = '600px'
  },

  /**
   * Recursively searches for an object by its ID within a nested data structure used in AG Grid.
   * This function traverses through hierarchical data structures where objects can contain 
   * nested child objects in a 'nestedData' property array.
   * 
   * @function findObjectByIdInNestedDataInAggridData
   * @param {Array<Object>} data - Array of objects to search through. Each object should have an 'id' property
   *                               and may optionally contain a 'nestedData' array with child objects.
   * @param {string|number} targetId - The ID of the object to find. Can be string or number as the function
   *                                   converts both to strings for comparison.
   * @returns {Object|null} Returns the found object if a match is found, otherwise returns null.
   */
  findObjectByIdInNestedDataInAggridData : (data, targetId) => {
    for (let item of data) {
        // Check if current item matches the target ID
        if (item.id.toString() === targetId.toString()) {
            return item;
        }

        // If item has nestedData, recursively search through it
        if (item.nestedData && Array.isArray(item.nestedData)) {
            const found = Utils.findObjectByIdInNestedDataInAggridData(item.nestedData, targetId);
            if (found) {
                return found;
            }
        }
    }

    return null; // Return null if not found
  },

  saveRouteChange: (path) => {
      window.OFM.isDev=true
      window.OFM.routeChanged(path);
  },

  getRouteParam: (name) => {
    if (name === 'clientid' && getEnvironment() === 'local' && CookieService.getCookie("clientId")) {
      return CookieService.getCookie("clientId");
    }

    const search = window.location.search;
    const params = new URLSearchParams(search);
    if (name === 'clientid') {
      return params.get(name) || sessionStorage.getItem("clientid");
    } else {
      return params.get(name);
    }
  },

  /**
   * Sanitizes a cookie name by removing all non-alphanumeric characters except for underscores and hyphens.
   * @param {string} name - The cookie name to sanitize.
   * @returns {string} The sanitized cookie name.
   */
  sanitizeCookieName: (name) => {
    return name.replace(/[^a-zA-Z0-9_-]/g, '');
  },

  /**
   * Sanitizes a string by converting single quotes to double quotes.
   * @param {string} str - The string to sanitize.
   * @returns {string} The sanitized string.
   */
  convertSingleToDoubleQuotes: (input) => {
    // Use a unique placeholder that's unlikely to appear in the input
    const placeholder = '___DOUBLE_QUOTE_PLACEHOLDER___';
    input = input.replaceAll('"', placeholder); // Store existing double quotes
    input = input.replace(/'([^']*?)'\s*:/g, '"$1":'); // Convert 'key': to "key":
    input = input.replace(/:\s*'([^']*?)'([,\]}])/g, ':"$1"$2'); // Convert : 'value' to : "value"
    input = input.replaceAll(placeholder, '"'); // Restore original double quotes
    return input;
  },

  /**
   * Sanitize string to valid JSON
   * @param {string} input - The string to sanitize.
   * @returns {string} The sanitized string.
   */
  sanitizeStringToValidJson: (input) => {
    let data = Utils.convertSingleToDoubleQuotes(input);
    data = data.replace(/None/g, 'null');
    data = data.replace(/False/g, 'false');
    data = data.replace(/True/g, 'true');
    return data;
  },

  /**
   * Converts a date string in "MMM d, yyyy" format to "yyyy-mm-dd" format.
   * This utility method transforms human-readable date strings (e.g., "Aug 6, 2025") 
   * into ISO date format (yyyy-mm-dd) suitable for API calls, database storage, 
   * and form inputs. The method includes comprehensive error handling and validation.
   * 
   * @param {string} dateString - The date string to convert. Expected formats:
   *   - "MMM d, yyyy" (e.g., "Aug 6, 2025")
   *   - "MMM dd, yyyy" (e.g., "Aug 06, 2025") 
   *   - "Month d, yyyy" (e.g., "August 6, 2025")
   *   - Any valid date string parseable by JavaScript Date constructor
   * 
   * @returns {string} The formatted date in "yyyy-mm-dd" format, or empty string if invalid
   *   - Success: Returns formatted date string (e.g., "2025-08-06")
   *   - Invalid input: Returns empty string ("")
   *   - Null/undefined input: Returns empty string ("")
   */
  convertToYYYYMMDD: (dateString) => {
    try {
      if (!dateString) return '';

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return '';
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error converting date:', error);
      return '';
    }
  },

  checkForClientCodeAndBuyingSystem : (systemParams, campaignClientCode) => {
    for(let i=0;i<systemParams?.dataArray?.length;i++){
      if(systemParams.dataArray[i]?.clientCode === campaignClientCode){
        if(['MediaOcean', 'MediaOcean-CA'].includes(systemParams.dataArray[i]?.buyingSystem)) {
          return true;
        }
        break;
      }
    }

    return false;
  },
  /**
   * Convert "YYYY-MM-DD" to MMM DD format.
   * @param {string} dateString - Date string in "YYYY-MM-DD" format.
   * @returns {string} The MMM DD format date.
   */
  convertDateToDisplayFormat: (dateString) => {
    const date = new Date(dateString);
    const options = { month: "long", day: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  },

  /**
   * Calculates the number of days left between today and the given date string.
   * @param {string} dateString - Date string in "YYYY-MM-DD" format.
   * @returns {number} The number of days left. It will show negative number if the date is in the past
   */
  calculateDaysLeft: (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },

  /**
   * Formats a number as a currency string in USD format.
   * @param {number} number - The number to format.
   * @returns {string} The formatted currency string.
   */
  formatNumberToUSDCurrency: (number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(number)
  }
};
